import './LinenParCalculator.css'
import { Helmet } from 'react-helmet-async';
import ArrowRight from '../icons/ArrowRight';
import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useLanguagePrefix from '../LanguagePrefix';
import Slider from '@mui/material/Slider';
import WhyChooseOurPms from '../components/WhyChooseOurPms';

function LinenParCalculator() {
    const langPrefix = useLanguagePrefix();
    const { t } = useTranslation();

    const [totalRooms, setTotalRooms] = useState(60);
    const [occupancy, setOccupancy] = useState(70); // %
    const [alos, setAlos] = useState(2.5); // nights
    const [turnaroundDays, setTurnaroundDays] = useState(1.2);
    const [safetyDays, setSafetyDays] = useState(0.5);
    const [lossPct, setLossPct] = useState(2); // % per month
    const [seasonalityPct, setSeasonalityPct] = useState(10); // % uplift

    // Mode
    const [mode, setMode] = useState("data"); // "data" | "heuristic"
    const [parFactor, setParFactor] = useState(4); // 3..5 for heuristic

    // Items
    const [items, setItems] = useState([
        { id: cryptoRandom(), name: "Bath Towel", piecesPerRoom: 2, policy: "everyNight", enabled: true },
        { id: cryptoRandom(), name: "Hand Towel", piecesPerRoom: 2, policy: "everyNight", enabled: true },
        { id: cryptoRandom(), name: "Bath Mat", piecesPerRoom: 1, policy: "everyNight", enabled: true },
        { id: cryptoRandom(), name: "Sheet Set", piecesPerRoom: 1, policy: "everyN", nNights: 2, enabled: true },
        { id: cryptoRandom(), name: "Pillowcase", piecesPerRoom: 2, policy: "everyN", nNights: 2, enabled: true },
    ]);

    const occupiedRooms = useMemo(
        () => (totalRooms || 0) * ((occupancy || 0) / 100),
        [totalRooms, occupancy]
    );

    function changeFactor(it) {
        if (it.policy === "everyNight") return 1;
        if (it.policy === "everyN") return it.nNights && it.nNights > 0 ? 1 / it.nNights : 1;
        // checkout only
        return alos > 0 ? 1 / alos : 1; // if unknown ALOS, assume daily change (conservative)
    }

    function dailyUse(it) {
        // DailyUse ≈ OccupiedRooms × PiecesPerRoom × ChangeFactor
        return occupiedRooms * (it.piecesPerRoom || 0) * changeFactor(it);
    }

    function monthlyLoss(daily) {
        // LossAllowance (month) ≈ dailyUse × 30 × lossRate%
        return daily * 30 * ((lossPct || 0) / 100);
    }

    function recommendedQty(it) {
        const dUse = dailyUse(it);
        // RecommendedQty = dailyUse × (turnaround + safety) × (1 + seasonality) + lossAllowance
        const opStock = dUse * ((turnaroundDays || 0) + (safetyDays || 0));
        const withSeason = opStock * (1 + (seasonalityPct || 0) / 100);
        const loss = monthlyLoss(dUse);
        return Math.ceil(withSeason + loss);
    }

    function heuristicQty(it) {
        // HeuristicQty = FullHouseSetQty × PARFactor
        const fullHouse = (totalRooms || 0) * (it.piecesPerRoom || 0);
        return Math.ceil(fullHouse * (parFactor || 3));
    }

    const rows = items
        .filter((i) => i.enabled)
        .map((it) => {
            const dUse = dailyUse(it);
            const rec = mode === "data" ? recommendedQty(it) : heuristicQty(it);
            const setsEquivalent = it.piecesPerRoom > 0 ? Math.ceil(rec / it.piecesPerRoom) : 0;
            const reorderPoint = Math.ceil(dUse * ((turnaroundDays || 0) + (safetyDays || 0)));
            return { it, dUse, rec, setsEquivalent, reorderPoint };
        });

    const totals = rows.reduce(
        (acc, r) => {
            acc.dailyUse += r.dUse;
            acc.recommended += r.rec;
            acc.sets += r.setsEquivalent;
            acc.reorder += r.reorderPoint;
            return acc;
        },
        { dailyUse: 0, recommended: 0, sets: 0, reorder: 0 }
    );

    function updateItem(id, patch) {
        setItems((prev) => prev.map((x) => (x.id === id ? { ...x, ...patch } : x)));
    }

    function addItem() {
        setItems((prev) => [
            ...prev,
            { id: cryptoRandom(), name: "New Item", piecesPerRoom: 1, policy: "everyNight", enabled: true },
        ]);
    }

    function removeItem(id) {
        setItems((prev) => prev.filter((x) => x.id !== id));
    }

    function cryptoRandom() {
        if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
        return Math.random().toString(36).slice(2);
    }

    function fmt(n) {
        if (!Number.isFinite(n)) return "-";
        if (Math.abs(n) >= 1000) return Math.round(n).toLocaleString();
        return (Math.round(n * 10) / 10).toString();
    }

    return (
        <div>
            <Helmet>

                <title>Hotel Linen PAR Calculator – Optimize Linen Inventory</title>
                <meta name="description" content="Calculate the ideal linen stock for your hotel with our free Linen PAR Calculator. Reduce costs, boost efficiency, and ensure top guest satisfaction." />
                <link rel="canonical" href={"https://www.prostay.com" + langPrefix + "/linen-par-calculator/"} />

                <link rel="alternate" href="https://www.prostay.com/es/linen-par-calculator/" />
                <link rel="alternate" href="https://www.prostay.com/en/linen-par-calculator/" />

                <meta property="og:title" content="Hotel Linen PAR Calculator – Optimize Linen Inventory" />
                <meta property="og:description" content="Calculate the ideal linen stock for your hotel with our free Linen PAR Calculator. Reduce costs, boost efficiency, and ensure top guest satisfaction." />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={"https://www.prostay.com" + langPrefix + "/linen-par-calculator/"} />
                <meta property="og:image" content='https://prostay.com/images/ogImage.png' />

                <meta property="og:site_name" content="Prostay" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Hotel Linen PAR Calculator – Optimize Linen Inventory" />
                <meta name="twitter:description" content="Calculate the ideal linen stock for your hotel with our free Linen PAR Calculator. Reduce costs, boost efficiency, and ensure top guest satisfaction." />
                <meta name="twitter:image" content='https://prostay.com/images/ogImage.png' />
                <meta name="twitter:site" content="@prostaypms" />
                <meta name="twitter:creator" content="@prostaypms" />
            </Helmet>

            <div className='linen-calculator d-flex justify-content-center'>
                <div className='d-flex flex-column page-content'>
                    <h1 style={{ margin: '0px' }}>
                        {t('Hotel Linen PAR Calculator – Calculate Your Optimal Linen Inventory')}
                    </h1>
                    <p className='mt-4'>{t('Calculate recommend on-hand qualities by linen type using either a data-driven model or the quick 3-5 PER heuristic. Adjust policy, laundry turnaround, safety stock, seasonality, and loss to fit your property. ')}</p>
                    <div className='d-flex flex-column flex-md-row mt-2 gap-3'>
                        <div className='col card'>
                            <h3 style={{ fontSize: 20 }}>{t('Property & Demand')}</h3>
                            <label className='mt-3 mb-1'>{t('Total rooms')}</label>
                            <input min={0} type='number' value={totalRooms} onChange={(e) => setTotalRooms(Number(e.target.value))} />
                            <label className='mt-3 mb-1'>{t('Average Occupancy (%)')}</label>
                            <input min={0} type='number' value={occupancy} onChange={(e) => setOccupancy(Number(e.target.value))} />
                            <label className='mt-3 mb-1'>{t('Average Length of Stay (nights)')}</label>
                            <input min={1} type='number' step={0.1} value={alos} onChange={(e) => setAlos(Number(e.target.value))} />
                            <span className='mt-3' style={{ color: '#606576', fontSize: 14, fontFamily: 'Inter' }}>{t('Occupied rooms/day:')} <b>{fmt(occupiedRooms)}</b></span>
                        </div>
                        <div className='col card'>
                            <h3 style={{ fontSize: 20 }}>{t('Operations & Buffers')}</h3>
                            <label className='mt-3 mb-1'>{t('Laundry turnaround (days)')}</label>
                            <input min={0} step={0.1} type='number' value={turnaroundDays} onChange={(e) => setTurnaroundDays(Number(e.target.value))} />
                            <label className='mt-3 mb-1'>{t('Safety stock (days)')}</label>
                            <input min={0} step={0.1} type='number' value={safetyDays} onChange={(e) => setSafetyDays(Number(e.target.value))} />
                            <label className='mt-3 mb-1'>{t('Loss/Damage (% per month)')}</label>
                            <input min={0} type='number' step={0.1} value={lossPct} onChange={(e) => setLossPct(Number(e.target.value))} />
                            <label className='mt-3 mb-1'>{t('Seasonality uplift (%)')}</label>
                            <input min={0} type='number' step={0.1} value={seasonalityPct} onChange={(e) => setSeasonalityPct(Number(e.target.value))} />
                        </div>
                        <div className='col card'>
                            <h3 style={{ fontSize: 20 }}>{t('Mode')}</h3>
                            <div className="d-flex align-items-center gap-3 mt-3">
                                <button className={`mode-btn ${mode === "data" ? "selected" : ""}`} onClick={() => setMode("data")}>
                                    Data-driven
                                </button>
                                <button className={`mode-btn ${mode === "heuristic" ? "selected" : ""}`} onClick={() => setMode("heuristic")}>
                                    Heuristic (3–5 PAR)
                                </button>
                            </div>
                            {mode === "heuristic" && (
                                <div className="mt-4">
                                    <p style={{ color: '#606576', fontWeight: 500 }}>
                                        PAR factor ({parFactor}x full-house set)
                                    </p>
                                    <Slider
                                        min={3}
                                        max={5}
                                        step={1}
                                        value={parFactor}
                                        onChange={(event, newValue) => setParFactor(newValue)}
                                        valueLabelDisplay="auto"
                                    />
                                    <p className=" mt-3" style={{ color: '#606576', fontWeight: 500 }}>
                                        {t('Heuristic uses:')} Qty = ({t('Rooms × Pieces/Room')}) × PAR
                                    </p>
                                </div>
                            )}
                            {mode === "data" && (
                                <p className="text-sm mt-3" style={{ color: '#606576', fontWeight: 500 }}>
                                    {t('Data-driven uses: DailyUse = OccRooms x Pieces/room x ChangeFactor; Recommend = DailyUse x (Turnaround + Safety) x (1+Seasonality) + MonthlyLoss')}
                                </p>
                            )}
                        </div>
                    </div>

                    <div className='card mt-4'>
                        <div className='d-flex w-100 align-items-center justify-content-between'>
                            <h3 className='m-0' style={{ fontSize: 20 }}>{t('Linen Items')}</h3>
                            <button className='light-blue-filled-btn-small' onClick={addItem}>
                                <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M5 1V5M5 5V9M5 5H9M5 5H1" stroke="white" stroke-linecap="round" stroke-linejoin="round" />
                                </svg>
                                {t('Add Item')}
                            </button>
                        </div>

                        <div className='grid mt-3'>
                            <div className='header-col'>
                                {t('Name')}
                            </div>
                            <div className='header-col'>
                                {t('Pieces/Room')}
                            </div>
                            <div className='header-col'>
                                {t('Change Policy')}
                            </div>
                            <div className='header-col'>
                                {t('Daily Use')}
                            </div>
                            <div className='header-col'>
                                {t('Recommended QTY')}
                            </div>
                            <div className='header-col'>
                                {t('Sets EQV.')}
                            </div>
                            <div className='header-col'>
                                {t('Reorder Point')}
                            </div>
                            <div className='header-col'>

                            </div>
                            {
                                rows.map(({ it, dUse, rec, setsEquivalent, reorderPoint }) => (
                                    <>
                                        <React.Fragment>
                                            <div className='grid-col'>
                                                <input
                                                    value={it.name}
                                                    onChange={e => updateItem(it.id, { name: e.target.value })}
                                                />
                                            </div>
                                            <div className='grid-col'>
                                                <input value={it.piecesPerRoom} onChange={(e) => updateItem(it.id, { piecesPerRoom: Number(e.target.value) })} min={0} />
                                            </div>
                                            <div className='grid-col d-flex flex-column'>
                                                <select
                                                    className="px-2 py-1 rounded-md border"
                                                    value={it.policy}
                                                    onChange={e => updateItem(it.id, { policy: e.target.value })}>
                                                    <option value="everyNight">{t('Change every stayover night')}</option>
                                                    <option value="everyN">{t('Change every N nights')}</option>
                                                    <option value="checkoutOnly">{t('Change on checkout only')}</option>
                                                </select>
                                                {it.policy === "everyN" && (
                                                    <div className="d-flex align-items-center">
                                                        <span className="text-xs text-neutral-600">N =</span>
                                                        <input value={it.nNights ?? 2} onChange={v => updateItem(it.id, { nNights: v })} min={1} />
                                                    </div>
                                                )}
                                            </div>
                                            <div className='grid-col'>
                                                {fmt(dUse)}
                                            </div>
                                            <div className='grid-col'>
                                                {fmt(rec)}
                                            </div>
                                            <div className='grid-col'>
                                                {fmt(setsEquivalent)}
                                            </div>
                                            <div className='grid-col'>
                                                {fmt(reorderPoint)}
                                            </div>
                                            <div className='grid-col'>
                                                <button onClick={() => removeItem(it.id)} style={{ fontSize: 12, color: '#606576', border: '1px solid #EAECF0', borderRadius: 4, backgroundColor: 'transparent', }}>{t('Remove')}</button>
                                            </div>
                                        </React.Fragment>
                                    </>
                                ))
                            }
                        </div>

                        <div className='d-flex flex-column flex-md-row w-100 mt-3 gap-3'>
                            <div className='col gray-card'>
                                <label>{t('Total Daily Use')}</label>
                                <span>{fmt(totals.dailyUse)}</span>
                            </div>
                            <div className='col gray-card'>
                                <label>{t('Total Recommended Qty')}</label>
                                <span>{fmt(totals.recommended)}</span>
                            </div>
                            <div className='col gray-card'>
                                <label>{t('Total Sets Equivalent')}</label>
                                <span>{fmt(totals.sets)}</span>
                            </div>
                            <div className='col gray-card'>
                                <label>{t('Total Reorder Point')}</label>
                                <span>{fmt(totals.reorder)}</span>
                            </div>
                        </div>
                    </div>

                    <small className='mt-2'>
                        {t('Note: “PAR” reflects stock needed to cover in-room usage, items in laundry, and shelf stock. Heuristic mode provide a quick 3-5x full-house guide; data-driven mode models occupancy change frequency, laundry turnaround, safety stock, loss, and seasonality.')}
                    </small>

                    <p className='mt-4' style={{ color: '#000000' }}>
                        {t('Managing linen inventory is critical for smooth hotel operations, cost control, and guest satisfaction. Prostay’s Hotel Linen PAR Calculator helps you determine exactly how many linens your property needs — balancing operational efficiency, laundry turnaround times, and seasonal demand. Avoid overstocking, prevent shortages, and keep your housekeeping team fully equipped.')}
                    </p>

                    <h4 className='my-3'>{t('What Is a Linen PAR Level?')}</h4>
                    <p className='m-0' style={{ color: '#000000' }}>
                        {t('In hospitality, PAR (Periodic Automatic Replacement) level refers to the standard number of linen sets needed to service all rooms while accounting for linens in use, in laundry, and in storage.')}
                        <br /> {t('A common rule of thumb is 3–5 PAR:')}
                    </p>
                    <ul>
                        <li>{t('1 PAR in rooms')}</li>
                        <li>{t('1 PAR in laundry')}</li>
                        <li>{t('1–3 PAR in storage or rotation')}</li>
                    </ul>
                    <p style={{ color: '#000000' }}>{t('However, the ideal number varies by property size, occupancy rate, change policies, laundry turnaround, loss rates, and seasonal fluctuations. This calculator uses data-driven formulas to give you a precise, customized recommendation.')}</p>

                    <div className='d-flex flex-column flex-md-row gap-4'>
                        <div className='col-12 col-md-7'>
                            <h5 className='my-4'>{t('Why Use Prostay’s Linen PAR Calculator?')}</h5>
                            <ul>
                                <li>{t('Accurate & Customizable: Goes beyond the generic “3–5 PAR” rule by factoring in your actual operations.')}</li>
                                <li>{t('Prevents Overstocking: Reduces unnecessary capital tied up in linen inventory.')}</li>
                                <li>{t('Prevents Shortages: Keeps enough stock to maintain guest comfort even during high occupancy.')}</li>
                                <li>{t('Supports Budget Planning: Helps forecast linen purchases and replacement cycles.')}</li>
                                <li>{t('Part of the Prostay PMS Suite: Save and manage results within your hotel’s operational tools.')}</li>
                            </ul>
                        </div>
                        <div className='col'>
                            <h5 className='my-4'>{t('Benefits of Optimizing Your Hotel Linen Inventory')}</h5>
                            <ul>
                                <li>{t('Lower storage and replacement costs')}</li>
                                <li>{t('Reduced environmental impact through better resource management')}</li>
                                <li>{t('Improved housekeeping efficiency')}</li>
                                <li>{t('Consistent guest experience and room readiness')}</li>
                            </ul>
                        </div>
                    </div>


                    <div className='mt-5'>
                        <WhyChooseOurPms
                            headerTitle="Take Charge with Hotel PMS Today!"
                            headerDescription="Cutting-edge Property Management System designed to revolutionize the way hotels, and resorts from boutique to large-scale, operate and serve their guests."
                            sectionTitle={t("Why Choose Our Hotel Sales Software?")}
                            sectionDescription={t("Our platform is the preferred choice for hotels, hostels, vacation rentals, and alternative accommodations across the globe. Experience the difference with a PMS designed for growth, efficiency, and guest satisfaction.")}
                            cards={[
                                { title: t("How do I calculate the correct linen PAR level?"), description: t('A basic formula is: PAR Level = (Daily Linen Requirement × Laundry Turnaround Days) + Safety Stock Prostay’s calculator refines this by also factoring in occupancy rate, change frequency, loss rates, and seasonality.') },
                                { title: t("What is the industry standard for hotel linen PAR levels?"), description: t("Most hotels operate with 3–5 PAR, but the exact level should be based on your occupancy, laundry turnaround, and operational policies.") },
                                { title: t("How often should I review my hotel linen PAR levels?"), description: t("At least twice a year, or whenever there’s a change in occupancy patterns, laundry processes, or service standards.") },
                                { title: t("Can I use this calculator for other textiles like uniforms or spa linens?"), description: t("Yes — simply add them as custom items with the correct “pieces per room” or per service point.") },
                                { title: t("What does PAR stand for in hotel linen management?"), description: t("In hospitality, PAR stands for Periodic Automatic Replacement. It refers to the number of complete linen sets required to service all rooms while accounting for items in use, in laundry, and in storage.") },
                            ]}
                        />
                    </div>

                </div>
            </div>
        </div>
    )
}

export default LinenParCalculator;